import { PageHeader } from '../components/PageHeader';
import SortableTable from '../components/SortableTable';
import { useQuery } from '@tanstack/react-query';
import backend from '../services/backend';
import { ExclamationTriangleIcon, PlusIcon, PuzzlePieceIcon } from '@heroicons/react/24/outline';
import { Link } from '../components/Link';
import { usePlatformData } from '../hooks/usePlatformData';
import clsx from 'clsx';
import { Button } from '../catalyst/button';
import { useNavigate } from 'react-router-dom';
import { Label } from '../catalyst/fieldset';
import { Input } from '../catalyst/input';
import { useMemo } from 'react';
import { Field } from '../catalyst/fieldset';
import type { ApiEndpoints } from '@avisoma/shared';
import type { UseQueryResult } from '@tanstack/react-query';
import { formatDateTime } from '../lib/dateFormat';
import { useFilterState } from '../components/filters/useFilterState';
import { MissingCategoriesAlert } from './supertypes/MissingCategoriesAlert';

interface SupertypesFilters {
  name: string;
}

const defaultFilters: SupertypesFilters = { name: '' };

export default function Supertypes() {
  const navigate = useNavigate();
  const supertypesQuery = useQuery({
    queryKey: ['supertypes'],
    queryFn: () => backend.listSupertypes().then((res) => res.data),
  });

  const { filters, setFilters, clear } = useFilterState<SupertypesFilters>({
    storageKey: 'supertypes-filters',
    defaults: defaultFilters,
    onFilterChange: () => {},
  });

  return (
    <div>
      {/* Header */}
      <PageHeader
        title="Supertypes"
        description="View your supertypes"
        icon={<PuzzlePieceIcon className="size-6" />}
        showBackButton={true}
      >
        <div className="flex items-center gap-4">
          <Button className="text-nowrap" onClick={() => navigate('/supertypes/new')}>
            <PlusIcon className="size-5" />
            Add Supertype
          </Button>
        </div>
      </PageHeader>

      {supertypesQuery.error && (
        <div className="mb-6 bg-white dark:bg-zinc-800 shadow rounded-lg p-6">
          <div className="text-center text-red-600 dark:text-red-400">
            {supertypesQuery.error instanceof Error ? supertypesQuery.error.message : 'Failed to load supertypes'}
          </div>
        </div>
      )}

      {!supertypesQuery.error && (
        <>
          <MissingCategoriesAlert supertypesQuery={supertypesQuery} />

          <Filters filters={filters} setFilters={setFilters} clear={clear} />

          <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
            <SupertypesTable nameFilter={filters.name} supertypesQuery={supertypesQuery} />
          </div>
        </>
      )}
    </div>
  );
}

function Filters({
  filters,
  setFilters,
  clear,
}: {
  filters: SupertypesFilters;
  setFilters: (value: SupertypesFilters) => void;
  clear: () => void;
}) {
  return (
    <div className="mb-6">
      <div className="flex gap-4 md:flex-row flex-col">
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4 w-full">
          {/* Filter: Name */}
          <Field>
            <Label htmlFor="filter-name">Name</Label>
            <Input
              id="filter-name"
              type="text"
              className="catalyst-input mt-1"
              placeholder="Filter by name"
              value={filters.name}
              onChange={(e) => setFilters({ ...filters, name: e.target.value })}
            />
          </Field>
        </div>

        <div className="flex items-end">
          <Button
            outline
            onClick={clear}
            className="whitespace-nowrap w-full md:w-auto"
            disabled={!filters.name}
          >
            Clear Filters
          </Button>
        </div>
      </div>
    </div>
  );
}

function SupertypesTable({
  nameFilter,
  supertypesQuery,
}: {
  nameFilter: string;
  supertypesQuery: UseQueryResult<ApiEndpoints<Date>['/api/supertypes']['GET']['response']>;
}) {
  const platformData = usePlatformData();
  // Filter the data based on name filter
  const filteredData = useMemo(() => {
    if (!supertypesQuery.data?.items) return [];

    return supertypesQuery.data.items.filter((item) => {
      // Name filter
      if (nameFilter && !item.name.toLowerCase().includes(nameFilter.toLowerCase())) {
        return false;
      }

      return true;
    });
  }, [supertypesQuery.data?.items, nameFilter]);

  const hasCategory = (category: string) => {
    if (!platformData.categories.data) {
      return true;
    }

    return platformData.categories.data?.pageCategories.includes(category);
  };

  return (
    <div className="overflow-hidden rounded-lg">
      <SortableTable
        storageId="supertypes"
        limitHeight={false}
        data={filteredData}
        columns={[
          {
            accessor: 'name',
            label: 'Name',
            sortable: true,
            render: (row) => (
              <strong>
                <Link to={`/supertypes/${row.id}`} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
                  {row.name}
                </Link>
              </strong>
            ),
          },
          {
            accessor: 'categoryScoreMultiplier',
            label: 'Category Score Multiplier',
            render: (row) => `${(row.specs.categoryScoreMultiplier * 100).toFixed(0)}%`,
          },
          {
            accessor: 'categories',
            label: 'Categories',
            render: (row) => (
              <ul>
                {row.specs.categories.map((category, index) => (
                  <li className={clsx({ 'text-red-500': !hasCategory(category[0]) })} key={index}>
                    {!hasCategory(category[0]) && (
                      <ExclamationTriangleIcon className="size-4 text-red-500 mr-1 inline-block" />
                    )}
                    {category[0]}
                    <span className="font-bold">{category[1] && ` (${category[1]}x)`}</span>
                  </li>
                ))}
              </ul>
            ),
          },
          {
            accessor: 'updatedAt',
            label: 'Updated At',
            sortable: true,
            render: (row) => formatDateTime(row.updatedAt),
          },
        ]}
        rowKey={(row) => row.id.toString()}
      />
    </div>
  );
}
